import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  ButtonIcon,
  Icon,
  Menu,
  type MenuOption,
} from '@saviynt/design-system';

import {
  ALLOWED_ACTIONS,
  LIST_ACTIONS,
  TABS_FILTERS,
  VIOLATIONS_TAB_LIST_ACTIONS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { detailsActions } from '../../../data-store/detailsSlice';

/**
 * Action buttons component for violations tab details view.
 * Renders primary action button and overflow menu with additional actions
 * based on the current tab state and mitigating control count.
 *
 * @param props - Component props
 * @returns JSX element containing action buttons or null if no tab is active
 */
function ViolationsTabDetailsActions() {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const {
    details: {
      violationsTab: { actionManager },
    },
    violationsTab: {
      controls: { tabInAction },
    },
  } = useTypedSelector((state) => state.aag);

  const [optionsWithIcons, setOptionsWithIcons] = useState([] as MenuOption[]);

  // Constants
  const actionsList = useMemo(() => {
    if (!actionManager) {
      return [];
    }

    const { allowedActions } = actionManager;

    if (!allowedActions) {
      return [];
    }

    const tempList: string[] = [];

    allowedActions.forEach((action) => {
      if (action in ALLOWED_ACTIONS) {
        tempList.push(
          ...ALLOWED_ACTIONS[action as keyof typeof ALLOWED_ACTIONS]
        );
      }
    });

    return tempList;
  }, [actionManager]);
  const getOptionDisabledStatus = useCallback(
    (value: string) => {
      if (actionManager?.isReadOnlyActions) {
        return !(
          value === LIST_ACTIONS.VIEWDETAILS.value ||
          value === LIST_ACTIONS.GENERATEREPORT.value
        );
      }

      return !actionsList.includes(value);
    },
    [actionManager?.isReadOnlyActions, actionsList]
  );

  // Populate the list actions
  useEffect(() => {
    if (tabInAction === null) {
      return;
    }

    const optionsList = VIOLATIONS_TAB_LIST_ACTIONS[tabInAction]
      .filter((o) => o.value !== LIST_ACTIONS.VIEWDETAILS.value)
      .map((o) => ({
        ...o,
        isDisabled: getOptionDisabledStatus(o.value),
      }));
    const sanitizedOptionList = optionsList.filter((o, index) => {
      if (o.isDivider && (index === 1 || index === optionsList.length - 1)) {
        return false;
      }

      return true;
    });

    const isMitigateCase = Boolean(
      tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value &&
        actionManager?.mitigatingControlCount &&
        Number(actionManager.mitigatingControlCount) > 1
    );

    // Case: Mitigated tab, not in review, count > 1
    if (isMitigateCase) {
      const updatedOptions = sanitizedOptionList.map((option) => {
        const isLabelWithCount = Boolean(
          option.value === LIST_ACTIONS.REMOVEMC.value ||
            option.value === LIST_ACTIONS.CHNAGEMCENDDATE.value
        );

        if (isLabelWithCount) {
          return {
            ...option,
            label: l(`${option.label} ({count})`, {
              count: actionManager?.mitigatingControlCount,
            }),
          };
        }

        return {
          ...option,
          label: l(option.label),
        };
      });

      setOptionsWithIcons(updatedOptions);

      return;
    }

    // Default case
    setOptionsWithIcons(sanitizedOptionList);
  }, [
    l,
    tabInAction,
    actionManager?.mitigatingControlCount,
    getOptionDisabledStatus,
  ]);

  const handleOptionClick = (option: { value: string }) => {
    dispatch(
      detailsActions.setViolationTabdetailsActionManager(
        actionManager
          ? {
              ...actionManager,
              isOpen: true,
              action: option.value,
            }
          : null
      )
    );
  };

  const primaryAction =
    optionsWithIcons.length > 0 ? optionsWithIcons[0] : null;
  const menuActions =
    optionsWithIcons.length > 1 ? optionsWithIcons.slice(1) : undefined;

  if (tabInAction === null) {
    return null;
  }

  return (
    <>
      <Button
        type='button'
        kind='outlined'
        size='medium'
        isDisabled={getOptionDisabledStatus(primaryAction?.value ?? '')}
        onClick={() => {
          handleOptionClick({ value: primaryAction?.value ?? '' });
        }}>
        {l(primaryAction?.label ?? '')}
      </Button>
      {menuActions && menuActions.length > 0 && (
        <Menu
          placement='bottom-end'
          options={menuActions}
          onChange={handleOptionClick}
          minOptionsForSearch={menuActions.length + 1}
          trigger={
            <ButtonIcon
              size='medium'
              kind='ghost'
              ariaLabel={l('More actions')}
              icon={<Icon kind='MoreVertical' />}
            />
          }
        />
      )}
    </>
  );
}

export default memo(ViolationsTabDetailsActions);
